/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Palette, Eye, Sparkles, FileText, Presentation, Lock, Unlock, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import { DEPT_STATUS, deptPath, downloadVersion, fileUrl, errMsg } from "../lib/reportApi";
import { fmtDateTimeIST, fmtDateOnlyIST } from "../lib/formatDateTime";

export function GenerateButton({ matchId, onDone, className = "" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/report/generate`);
      toast.success(`Version ${data.version} generated — ${data.page_count} pages`);
      onDone?.(data);
    } catch (e) { toast.error(errMsg(e, "Generation failed")); } finally { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy} data-testid="report-generate-btn"
      className={`bg-readyGreen text-white px-5 py-3 font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2 disabled:opacity-60 ${className}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Generating…" : "Generate Post Event Report"}
    </button>
  );
}

export function VersionList({ versions }) {
  const dl = async (v, f) => { try { await downloadVersion(v, f); } catch { toast.error("Download failed"); } };
  if (!versions.length) return <div className="text-sm text-zinc-500 p-4" data-testid="report-versions-empty">No reports generated yet.</div>;
  return (
    <ul className="divide-y divide-cmdBorder" data-testid="report-versions">
      {versions.map((v, i) => (
        <li key={v.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3" data-testid={`report-version-${v.version}`}>
          <div className="flex-1">
            <div className="font-head font-bold text-lg">Version {v.version} {i === 0 && <span className="text-[10px] font-sans tracking-widest uppercase text-readyGreen ml-1">latest</span>}</div>
            <div className="text-xs text-zinc-500">{fmtDateTimeIST(v.generated_at)} · {v.generated_by} · {v.page_count} pages · {v.item_count} items</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => dl(v, "pdf")} className="border border-incidentRed text-incidentRed px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest flex items-center gap-1" data-testid={`report-download-pdf-${v.version}`}><FileText className="w-3 h-3" /> PDF</button>
            <button onClick={() => dl(v, "pptx")} className="border border-warnAmber text-warnAmber px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest flex items-center gap-1" data-testid={`report-download-pptx-${v.version}`}><Presentation className="w-3 h-3" /> PowerPoint</button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function DeptRow({ d, onOpen }) {
  const st = DEPT_STATUS[d.status] || DEPT_STATUS.not_started;
  return (
    <tr onClick={onOpen} className="cursor-pointer hover:bg-surfaceHover" data-testid={`report-dept-row-${d.department}`}>
      <td>
        <div className="flex items-center gap-3">
          {d.thumb ? <img src={fileUrl(d.thumb)} alt="" className="w-12 h-9 object-cover bg-zinc-100" loading="lazy" /> : <div className="w-12 h-9 bg-zinc-100" />}
          <span className="font-medium">{d.department}</span>
        </div>
      </td>
      <td className="text-right font-mono-live" data-testid={`report-dept-count-${d.department}`}>{d.item_count}</td>
      <td className="text-right font-mono-live text-readyGreen">{d.approved_count}</td>
      <td><span className={`border px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold whitespace-nowrap ${st.cls}`}>{st.label}</span></td>
      <td className="text-xs text-zinc-500">{d.last_updated_at ? <>{fmtDateTimeIST(d.last_updated_at)}<div>{d.last_updated_by}</div></> : "—"}</td>
      <td className="text-right"><ChevronRight className="w-4 h-4 inline text-zinc-400" /></td>
    </tr>
  );
}

export default function PostEventReportPage() {
  const { selectedMatchId: M } = useMatch();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = async () => { const { data: d } = await api.get(`/matches/${M}/report`); setData(d); };
  useEffect(() => { if (M) load(); }, [M]);
  if (!M) return <RequireMatch />;
  if (!data) return <div className="p-8 text-zinc-500 font-mono-live">loading…</div>;

  const { match, departments, me, settings } = data;
  const totals = departments.reduce((a, d) => ({ items: a.items + d.item_count, approved: a.approved + d.approved_count, done: a.done + (d.status === "approved" ? 1 : 0) }), { items: 0, approved: 0, done: 0 });
  const toggleLock = async () => {
    try { await api.put(`/matches/${M}/report/settings`, { locked: !settings.locked }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="p-6 md:p-10 pb-24" data-testid="page-post-event-report">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Post-Match · Automated Report</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Post Event Report</h1>
          <div className="mt-3 text-sm" data-testid="report-match-details">
            <span className="font-semibold">{match.name}</span>
            <span className="text-zinc-500"> · {[match.team_a, match.team_b].filter(Boolean).join(" vs ")} · {fmtDateOnlyIST(match.match_date)} · {match.venue}{match.city ? `, ${match.city}` : ""}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {me.can_manage && <Link to="/post-event-report/design" className="border border-cmdBorder px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1" data-testid="report-design-link"><Palette className="w-3 h-3" /> Design settings</Link>}
          <Link to="/post-event-report/preview" className="border border-black px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1" data-testid="report-preview-link"><Eye className="w-3 h-3" /> Preview report</Link>
          {me.can_manage && (
            <button onClick={toggleLock} className={`border px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1 ${settings.locked ? "border-incidentRed text-incidentRed" : "border-cmdBorder"}`} data-testid="report-lock-btn">
              {settings.locked ? <><Lock className="w-3 h-3" /> Locked — unlock</> : <><Unlock className="w-3 h-3" /> Lock editing</>}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[["Departments", departments.length, "report-kpi-depts"], ["Report items", totals.items, "report-kpi-items"], ["Approved items", totals.approved, "report-kpi-approved"], ["Depts approved", `${totals.done}/${departments.length}`, "report-kpi-done"]].map(([l, v, t]) => (
          <div key={l} className="mc-panel p-4"><div className="mc-label">{l}</div><div className="mc-metric text-4xl mt-2" data-testid={t}>{v}</div></div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 mc-panel overflow-x-auto hidden md:block">
          <table className="w-full text-sm mc-table" data-testid="report-dept-table">
            <thead><tr className="text-left mc-label"><th className="p-3">Department</th><th className="p-3 text-right">Items</th><th className="p-3 text-right">Approved</th><th className="p-3">Status</th><th className="p-3">Last updated</th><th /></tr></thead>
            <tbody className="[&_td]:p-3 [&_tr]:border-t [&_tr]:border-cmdBorder">
              {departments.map((d) => <DeptRow key={d.department} d={d} onOpen={() => nav(deptPath(d.department))} />)}
            </tbody>
          </table>
        </div>
        <ul className="md:hidden space-y-2" data-testid="report-dept-cards">
          {departments.map((d) => {
            const st = DEPT_STATUS[d.status] || DEPT_STATUS.not_started;
            return (
              <li key={d.department} onClick={() => nav(deptPath(d.department))} className="mc-panel p-3 flex items-center gap-3" data-testid={`report-dept-card-${d.department}`}>
                {d.thumb ? <img src={fileUrl(d.thumb)} alt="" className="w-12 h-9 object-cover" /> : <div className="w-12 h-9 bg-zinc-100" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{d.department}</div>
                  <div className="text-xs text-zinc-500">{d.item_count} items · {d.approved_count} approved</div>
                </div>
                <span className={`border px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold ${st.cls}`}>{st.label}</span>
              </li>
            );
          })}
        </ul>
        <div className="space-y-4">
          {me.can_manage && <GenerateButton matchId={M} onDone={load} className="w-full" />}
          <div className="mc-panel">
            <div className="p-3 border-b border-cmdBorder mc-label">Generated versions</div>
            <VersionList versions={data.versions} />
          </div>
          <div className="text-xs text-zinc-500 px-1">
            {settings.include_unapproved ? "All items are included (administrator override)." : "Only approved items are included."} {settings.include_empty_departments ? "Empty departments are shown." : "Departments with no included items are left out."}
          </div>
        </div>
      </div>
    </div>
  );
}
